import { z } from 'zod';
import { NoteSchema, SWEEP_KINDS } from './notes.js';
import { TodoProposalSchema, TodoSchema } from './todos.js';
export const AlmanacInputSchema = z.union([TodoSchema, NoteSchema]);
export const DROP_CHANNEL_AUTHOR_FILTERS = ['operator', 'anyone'];
export const DropChannelSchema = z
    .object({
    channel_id: z.string().min(1),
    label: z.string().min(1),
    author_filter: z.enum(DROP_CHANNEL_AUTHOR_FILTERS),
    ack_emoji: z.string().min(1),
    done_emoji: z.string().min(1),
    enabled: z.boolean(),
    last_intake_at: z.string().nullable(),
    last_intake_ts: z.string().nullable(),
})
    .strict();
export const CreateDropChannelSchema = DropChannelSchema.pick({
    channel_id: true,
    label: true,
})
    .extend({
    author_filter: z.enum(DROP_CHANNEL_AUTHOR_FILTERS).default('operator'),
    ack_emoji: z.string().min(1).max(100).default('eyes'),
    done_emoji: z.string().min(1).max(100).default('white_check_mark'),
    enabled: z.boolean().default(true),
})
    .strict();
export const UpdateDropChannelSchema = CreateDropChannelSchema.omit({ channel_id: true }).partial();
export const PendingResearchReplySchema = z
    .object({
    note_id: z.string(),
    research_id: z.string(),
    artifact_href: z.string(),
    title: z.string(),
    summary: z.string(),
    source: z
        .object({
        channel_id: z.string(),
        ts: z.string(),
        permalink: z.string().optional(),
    })
        .passthrough(),
    published_at: z.string(),
    slack_reply_attempts: z.number().int().nonnegative(),
})
    .strict();
export const InputsSnapshotSchema = z
    .object({
    todos: z.array(TodoSchema),
    proposals: z.array(TodoProposalSchema),
    notes: z.array(NoteSchema),
    drop_channels: z.array(DropChannelSchema),
})
    .strict();
/**
 * A server-controlled platform announcement (new digest block types, feature
 * notes, temporary operating guidance) broadcast to every user's scheduled
 * sweeps via the sweep context. Admin-authored: rows live in the
 * `platform_note` table and are edited there directly — there is no agent
 * write path. Agents treat the body as runbook text for the current sweep.
 */
export const PlatformNoteSchema = z
    .object({
    note_id: z.string(),
    /** One or two sentences of plain operational instruction — no nested structure. */
    text: z.string(),
    /** Sweep kinds the note applies to; null = every sweep kind. */
    scopes: z.array(z.enum(SWEEP_KINDS)).nullable(),
    /** ISO start of the broadcast window; null = already active. */
    starts_at: z.string().nullable(),
    /** ISO end of the broadcast window; null = active until removed. */
    ends_at: z.string().nullable(),
    created_at: z.string(),
})
    .strict();
export const SweepContextSchema = z
    .object({
    sweep: z.enum(SWEEP_KINDS),
    generated_at: z.string(),
    todos: z.array(TodoSchema),
    guidance: z.array(NoteSchema),
    research_queue: z.array(NoteSchema),
    reminders_due: z.array(TodoSchema),
    platform_notes: z.array(PlatformNoteSchema),
    drop_channels: z.array(DropChannelSchema),
    pending_replies: z.array(PendingResearchReplySchema),
    truncated: z
        .object({
        todos: z.boolean(),
        guidance: z.boolean(),
        research_queue: z.boolean(),
        reminders_due: z.boolean(),
        platform_notes: z.boolean(),
        drop_channels: z.boolean(),
        pending_replies: z.boolean(),
    })
        .strict(),
})
    .strict();
